import React, { createContext, useReducer, useContext, ReactNode } from 'react';

/**
 * Application state management for the Mindful Therapy Group frontend.
 * 
 * This module provides a centralized state management system using React Context
 * and useReducer. It handles all application state including appointment details,
 * user selections, and UTM tracking parameters.
 * 
 * URL Parameters:
 * The application accepts the following URL parameters to pre-populate state:
 * - preferredProvider: Provider name (e.g., "Rick Stonecipher")
 * - preferredProviderCode: Provider code (e.g., "50099")
 * - providerHeadshot: Provider image URL
 * - stateString: State name (e.g., "Colorado")
 * - onlineIntake: Boolean flag for online intake
 * 
 * Example URL:
 * ?preferredProvider=Rick%20Stonecipher&preferredProviderCode=50099&onlineIntake=true&providerHeadshot=https://mindfultherapygroup.com/wp-content/uploads/2025/06/50099-Derrick-Stonecipher-Headshot.jpg&stateString=Colorado
 */

/**
 * Availability - Represents a provider availability slot.
 *
 * Contains information about provider availability including contact details,
 * scheduling information, location, and services offered.
 */
export type Availability = {
  contactId: number;
  providerCode: string;
  providerType: string;
  firstName: string;
  lastName: string;
  startDate: string;
  startDateIoi: string;
  interval: string;
  dayOfWeek: string;
  startTimeSource: string;
  startTime: string;
  intakeType: string;
  locationType: string;
  locationCode: string;
  locationName: string;
  region: string;
  state: string;
  providerServices: string;
  insuranceInNetwork: string | null;
  specialties: string;
  ageRangeGroups: string;
  availabilityId?: number;
};

/**
 * AppStateType - The complete application state interface.
 *
 * Contains all state properties for the appointment booking flow including
 * user selections, appointment details, tracking parameters, and availabilities.
 */
export type AppStateType = {
  visitType: string | null;
  location: string | null;
  locationCode: string | null;
  locationAddress: string | null;
  service: string | null;
  reason: string | null;
  insurance: string | null;
  appointmentDate: string | null;
  appointmentTime: string | null;
  appointmentDay: string | null;
  reservationStartTime: number | null;
  ageGroup: string | null;
  existingClient: string | null;
  stateString: string | null;
  columnId: number | null;
  profileId: number | null;
  licenseKey: number | null;
  phone: string | null;
  relationship: string | null;
  selfReportedSource: string | null;
  textConsent: boolean | null;
  // Modalities from IOI_Payload (cs_modalities), stored as modalities
  modalities: string | null;
  // Uploaded images
  uploadedImages: { [key: string]: string } | null;
  // Availabilities data
  availabilities: Availability[] | null;
  availableLocations: any[] | null;
  selectedAvailability: Availability | null;
  // Session data
  sessionData: any | null;
  mtgSession: string | null;
  // Configuration
  remoteApiUrl: string | null;
  providerId: string | null;
  // Timer warning modal
  timerWarningModalDismissed: boolean;
  // Loading state
  isLoading: boolean;
  loadingStep: string | null;
  loadingProgress: number;
};

/**
 * Action - Union type for all possible state actions.
 *
 * Defines all actions that can be dispatched to modify the application state.
 * Each action has a type and optional payload for updating specific state properties.
 */
type Action =
  | { type: 'SET_VISIT_TYPE'; payload: string }
  | { type: 'SET_LOCATION'; payload: string }
  | { type: 'SET_LOCATION_CODE'; payload: string }
  | { type: 'SET_LOCATION_ADDRESS'; payload: string }
  | { type: 'SET_SERVICE'; payload: string }
  | { type: 'SET_REASON'; payload: string }
  | { type: 'SET_INSURANCE'; payload: string }
  | { type: 'SET_APPOINTMENT_DATE'; payload: string }
  | { type: 'SET_APPOINTMENT_TIME'; payload: string }
  | { type: 'SET_APPOINTMENT_DAY'; payload: string }
  | { type: 'SET_RESERVATION_START_TIME'; payload: number | null }
  | { type: 'SET_AGE_GROUP'; payload: string }
  | { type: 'SET_EXISTING_CLIENT'; payload: string }
  | { type: 'SET_STATE_STRING'; payload: string }
  | { type: 'SET_COLUMN_ID'; payload: number | null }
  | { type: 'SET_PROFILE_ID'; payload: number | null }
  | { type: 'SET_LICENSE_KEY'; payload: number | null }
  | { type: 'SET_PHONE'; payload: string }
  | { type: 'SET_RELATIONSHIP'; payload: string }
  | { type: 'SET_SELF_REPORTED_SOURCE'; payload: string | null }
  | { type: 'SET_TEXT_CONSENT'; payload: boolean }
  | { type: 'SET_MODALITIES'; payload: string }
  | { type: 'SET_UPLOADED_IMAGE'; payload: { key: string; filename: string } }
  | { type: 'SET_AVAILABILITIES'; payload: Availability[] }
  | { type: 'SET_AVAILABLE_LOCATIONS'; payload: any[] }
  | { type: 'SET_SELECTED_AVAILABILITY'; payload: Availability | null }
  | { type: 'SET_SESSION_DATA'; payload: any }
  | { type: 'SET_MTG_SESSION'; payload: string }
  | { type: 'SET_REMOTE_API_URL'; payload: string }
  | { type: 'SET_PROVIDER_ID'; payload: string }
  | { type: 'DISMISS_TIMER_WARNING_MODAL' }
  | { type: 'RESET_TIMER_WARNING_MODAL' }
  | { type: 'SET_LOADING_STATE'; payload: { isLoading: boolean; loadingStep: string | null; loadingProgress: number } }
  | { type: 'RESET' };

/**
 * Initial application state.
 *
 * Provides default values for all state properties. Most values are initially
 * set to null to indicate no selection has been made. preferredProvider is
 * set to 'Jane Doe' as the default provider name.
 */
const initialState: AppStateType = {
  visitType: null,
  location: null,
  locationCode: null,
  locationAddress: null,
  service: null,
  reason: null,
  insurance: null,
  appointmentDate: null,
  appointmentTime: null,
  appointmentDay: null,
  reservationStartTime: null,
  ageGroup: null,
  existingClient: null,
  stateString: null,
  columnId: null,
  profileId: null,
  licenseKey: null,
    phone: null,
    relationship: null,
    selfReportedSource: null,
    textConsent: null,
    modalities: null,
    uploadedImages: null,
  availabilities: null,
  availableLocations: null,
  selectedAvailability: null,
  sessionData: null,
  mtgSession: null,
  remoteApiUrl: null,
  providerId: null,
  timerWarningModalDismissed: false,
  isLoading: true,
  loadingStep: 'session/continue',
  loadingProgress: 10,
};

/**
 * App state reducer function.
 *
 * Handles all state updates based on dispatched actions. Each action type
 * updates specific properties in the state while preserving other values.
 * The reducer is pure and deterministic, making state changes predictable.
 *
 * @param state - Current application state
 * @param action - Action to be processed
 * @returns New state after applying the action
 *
 * @example
 * ```typescript
 * const newState = reducer(currentState, { type: 'SET_PREFERRED_PROVIDER', payload: 'Dr. Smith' });
 * ```
 */
function reducer(state: AppStateType, action: Action): AppStateType {
  switch (action.type) {
    case 'SET_VISIT_TYPE': return { ...state, visitType: action.payload };
    case 'SET_LOCATION': return { ...state, location: action.payload };
    case 'SET_LOCATION_CODE': return { ...state, locationCode: action.payload };
    case 'SET_LOCATION_ADDRESS': return { ...state, locationAddress: action.payload };
    case 'SET_SERVICE': return { ...state, service: action.payload };
    case 'SET_REASON': return { ...state, reason: action.payload };
    case 'SET_INSURANCE': return { ...state, insurance: action.payload };
    case 'SET_APPOINTMENT_DATE': return { ...state, appointmentDate: action.payload };
    case 'SET_APPOINTMENT_TIME': return { ...state, appointmentTime: action.payload };
    case 'SET_APPOINTMENT_DAY': return { ...state, appointmentDay: action.payload };
    case 'SET_RESERVATION_START_TIME': return { ...state, reservationStartTime: action.payload };
    case 'SET_AGE_GROUP': return { ...state, ageGroup: action.payload };
    case 'SET_EXISTING_CLIENT': return { ...state, existingClient: action.payload };
    case 'SET_STATE_STRING': return { ...state, stateString: action.payload };
    case 'SET_COLUMN_ID': return { ...state, columnId: action.payload };
    case 'SET_PROFILE_ID': return { ...state, profileId: action.payload };
    case 'SET_LICENSE_KEY': return { ...state, licenseKey: action.payload };
    case 'SET_PHONE': return { ...state, phone: action.payload };
    case 'SET_RELATIONSHIP': return { ...state, relationship: action.payload };
    case 'SET_SELF_REPORTED_SOURCE': return { ...state, selfReportedSource: action.payload };
    case 'SET_TEXT_CONSENT': return { ...state, textConsent: action.payload };
    case 'SET_MODALITIES': return { ...state, modalities: action.payload };
    case 'SET_UPLOADED_IMAGE': 
      return { 
        ...state, 
        uploadedImages: { 
          ...state.uploadedImages, 
          [action.payload.key]: action.payload.filename 
        } 
      };
    case 'SET_AVAILABILITIES': return { ...state, availabilities: action.payload };
    case 'SET_AVAILABLE_LOCATIONS': return { ...state, availableLocations: action.payload };
    case 'SET_SELECTED_AVAILABILITY': return { ...state, selectedAvailability: action.payload };
    case 'SET_SESSION_DATA': return { ...state, sessionData: action.payload };
    case 'SET_MTG_SESSION': return { ...state, mtgSession: action.payload };
    case 'SET_REMOTE_API_URL': return { ...state, remoteApiUrl: action.payload };
    case 'SET_PROVIDER_ID': return { ...state, providerId: action.payload };
    case 'DISMISS_TIMER_WARNING_MODAL': return { ...state, timerWarningModalDismissed: true };
    case 'RESET_TIMER_WARNING_MODAL': return { ...state, timerWarningModalDismissed: false };
    case 'SET_LOADING_STATE': return { 
      ...state, 
      isLoading: action.payload.isLoading,
      loadingStep: action.payload.loadingStep,
      loadingProgress: action.payload.loadingProgress
    };
    case 'RESET': return initialState;
    default: return state;
  }
}

/**
 * AppStateContext - React Context for application state.
 *
 * Provides access to the application state and dispatch function throughout
 * the component tree. Components can consume this context to read state
 * and dispatch actions.
 */
const AppStateContext = createContext<{
  state: AppStateType;
  dispatch: React.Dispatch<Action>;
}>({ state: initialState, dispatch: () => {} });

/**
 * AppStateProvider - Context provider component for application state.
 *
 * Wraps the application and provides state management capabilities to all
 * child components. Uses useReducer to manage state and provides both
 * the current state and dispatch function through context.
 *
 * @param children - React components to be wrapped with state context
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <AppStateProvider>
 *       <YourAppComponents />
 *     </AppStateProvider>
 *   );
 * }
 * ```
 */
export const AppStateProvider = ({ children, initialState: customInitialState }: { children: ReactNode; initialState?: Partial<AppStateType> }) => {
  const [state, dispatch] = useReducer(reducer, { ...initialState, ...customInitialState });
  return (
    <AppStateContext.Provider value={{ state, dispatch }}>
      {children}
    </AppStateContext.Provider>
  );
};

/**
 * useAppState - Custom hook to access application state.
 *
 * Provides a convenient way to access the current state and dispatch function
 * from any component within the AppStateProvider.
 *
 * @returns Object containing current state and dispatch function
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { state, dispatch } = useAppState();
 *   
 *   const handleProviderSelect = (provider: string) => {
 *     dispatch({ type: 'SET_PREFERRED_PROVIDER', payload: provider });
 *   };
 *   
 *   return <div>Selected: {state.preferredProvider}</div>;
 * }
 * ```
 */
export function useAppState() {
  return useContext(AppStateContext);
}

export { reducer as appStateReducer, initialState }; 